
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWallet,
  faArrowDown,
  faArrowUp,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { formatCurrency, getAuthData } from '../../utils';

// ==========================================
// MAIN CONTAINER
// ==========================================

const StyledWalletSectionContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  position: relative;
  gap: 10px;
  flex-shrink: 0;
`;

// ==========================================
// DESKTOP WALLET SUMMARY
// ==========================================

const StyledWalletBalances = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #e8edf4;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(15, 23, 42, 0.035);

  @media (max-width: 600px) {
    display: none;
  }
`;

const StyledWalletCard = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 112px;
`;

const StyledWalletCardIcon = styled.div`
  width: 34px;
  height: 34px;
  min-width: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;

  background: ${({ $type }) =>
    $type === 'buyer' ? '#EFF6FF' : '#ECFDF5'};

  color: ${({ $type }) =>
    $type === 'buyer' ? '#2563EB' : '#059669'};

  font-size: 14px;
`;

const StyledWalletInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;

  span {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    color: #94A3B8;
    text-transform: uppercase;
    white-space: nowrap;
  }

  strong {
    font-size: 13px;
    font-weight: 750;
    color: #172033;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const StyledWalletDivider = styled.div`
  width: 1px;
  height: 32px;
  background: #E9EEF5;
  flex-shrink: 0;
`;

// ==========================================
// MOBILE WALLET BUTTON
// ==========================================

const StyledWalletMobileButton = styled.button`
  display: none;
  width: 42px;
  height: 42px;
  border: 1px solid #DBEAFE;
  border-radius: 10px;
  background: #EFF6FF;
  color: ${({ theme }) => theme.colors.primaryColor || '#2563EB'};
  cursor: pointer;
  font-size: 17px;
  transition: all 0.2s ease;

  &:hover,
  &.active {
    background: #DBEAFE;
  }

  @media (max-width: 600px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// ==========================================
// MOBILE WALLET PANEL
// ==========================================

const StyledMobileWalletPanel = styled.div`
  display: none;

  @media (max-width: 600px) {
    display: ${({ $open }) => $open ? 'block' : 'none'};
    position: fixed;
    top: 70px;
    right: 12px;
    width: min(340px, calc(100vw - 24px));
    background: #ffffff;
    border: 1px solid #E8EDF4;
    border-radius: 14px;
    box-shadow: 0 15px 40px rgba(15, 23, 42, 0.16);
    z-index: 9999;
    overflow: hidden;
    animation: walletOpen 0.2s ease;

    @keyframes walletOpen {
      from {
        opacity: 0;
        transform: translateY(-7px);
      }

      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

// ==========================================
// MOBILE PANEL HEADER
// ==========================================

const StyledMobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 18px;
  background: linear-gradient(120deg, #000000, #000000);
  color: #ffffff;

  div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  strong {
    font-size: 15px;
    font-weight: 700;
  }

  span {
    font-size: 11px;
    color: #BFDBFE;
  }

  button {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #ffffff;
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.22);
    }
  }
`;

// ==========================================
// MOBILE BALANCE LIST
// ==========================================

const StyledMobileBalances = styled.div`
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledMobileBalanceCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px;
  border: 1px solid ${({ $type }) =>
    $type === 'buyer' ? '#DBEAFE' : '#D1FAE5'};
  border-radius: 10px;
  background: ${({ $type }) =>
    $type === 'buyer' ? '#F8FAFF' : '#F7FEFA'};
`;

// ==========================================
// MOBILE FOOTER
// ==========================================

const StyledMobileFooter = styled.div`
  padding: 11px 15px;
  border-top: 1px solid #EDF0F5;
  background: #F8FAFC;

  span {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 11px;
    color: #94A3B8;
  }
`;

// ==========================================
// WALLET COMPONENT
// ==========================================

const WalletSection = ({
  gstBuyerWallet,
  gstSellerWallet,
  nonGstBuyerWallet,
  nonGstSellerWallet,
}) => {
  const { userProfile } = getAuthData();
  const { isGSTSeller } = userProfile;

  const [expandWallet, setExpandWallet] = useState(false);
  const walletRef = useRef(null);

  // Close mobile wallet on outside click
  const closeWalletList = useCallback((e) => {
    if (
      walletRef.current &&
      !walletRef.current.contains(e.target)
    ) {
      setExpandWallet(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', closeWalletList, true);

    return () => {
      document.removeEventListener('click', closeWalletList, true);
    };
  }, [closeWalletList]);

  // Preserve existing GST / Non-GST selection
  const walletItems = isGSTSeller === 'Yes'
    ? [
      {
        label: 'Buyer',
        value: gstBuyerWallet,
        icon: faArrowDown,
        type: 'buyer',
      },
      {
        label: 'Seller',
        value: gstSellerWallet,
        icon: faArrowUp,
        type: 'seller',
      },
    ]
    : [
      {
        label: 'Buyer',
        value: nonGstBuyerWallet,
        icon: faArrowDown,
        type: 'buyer',
      },
      {
        label: 'Seller',
        value: nonGstSellerWallet,
        icon: faArrowUp,
        type: 'seller',
      },
    ];

  return (
    <StyledWalletSectionContainer ref={walletRef}>

      {/* ==================================
          DESKTOP: INLINE WALLET BALANCES
      ================================== */}

      <StyledWalletBalances>
        {walletItems.map(({ label, value, icon, type }, index) => (
          <React.Fragment key={type}>

            {index > 0 && <StyledWalletDivider />}

            <StyledWalletCard>
              <StyledWalletCardIcon $type={type}>
                <FontAwesomeIcon icon={icon} />
              </StyledWalletCardIcon>

              <StyledWalletInfo>
                <span>{label}</span>
                <strong>{formatCurrency(value)}</strong>
              </StyledWalletInfo>
            </StyledWalletCard>

          </React.Fragment>
        ))}
      </StyledWalletBalances>

      {/* ==================================
          MOBILE: WALLET ICON
      ================================== */}

      <StyledWalletMobileButton
        type="button"
        className={expandWallet ? 'active' : ''}
        onClick={() => setExpandWallet(prev => !prev)}
        aria-label="View wallet balances"
        aria-expanded={expandWallet}
        title="Wallet Balance"
      >
        <FontAwesomeIcon icon={faWallet} />
      </StyledWalletMobileButton>

      {/* ==================================
          MOBILE: WALLET PANEL
      ================================== */}

      <StyledMobileWalletPanel $open={expandWallet}>

        <StyledMobileHeader>
          <div>
            <strong>My Wallet</strong>
            <span>Available wallet balances</span>
          </div>

          <button
            type="button"
            onClick={() => setExpandWallet(false)}
            aria-label="Close wallet"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </StyledMobileHeader>

        <StyledMobileBalances>
          {walletItems.map(({ label, value, icon, type }) => (
            <StyledMobileBalanceCard key={type} $type={type}>

              <StyledWalletCardIcon $type={type}>
                <FontAwesomeIcon icon={icon} />
              </StyledWalletCardIcon>

              <StyledWalletInfo>
                <span>{label} Balance</span>
                <strong>{formatCurrency(value)}</strong>
              </StyledWalletInfo>

            </StyledMobileBalanceCard>
          ))}
        </StyledMobileBalances>

        {/* <StyledMobileFooter>
          <span>
            <FontAwesomeIcon icon={faWallet} />
            Secure business wallet
          </span>
        </StyledMobileFooter> */}

      </StyledMobileWalletPanel>

    </StyledWalletSectionContainer>
  );
};

export default WalletSection;
